import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, getInspectionStatusColor, getInspectionStatusLabel, calculateProgress } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  FileCheck, 
  ClipboardList, 
  Clock, 
  CheckCircle2, 
  Plus, 
  ArrowRight,
  AlertTriangle,
  Loader2,
  Users,
  Bell
} from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const statCards = [
    { 
      title: 'Gesamt', 
      value: stats?.total || 0, 
      icon: FileCheck, 
      color: 'text-foreground',
      bgColor: 'bg-secondary'
    },
    { 
      title: 'Entwurf', 
      value: stats?.draft || 0, 
      icon: ClipboardList, 
      color: 'text-zinc-400',
      bgColor: 'bg-zinc-500/10'
    },
    { 
      title: 'In Bearbeitung', 
      value: stats?.in_progress || 0, 
      icon: Clock, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    { 
      title: 'Abgeschlossen', 
      value: stats?.completed || 0, 
      icon: CheckCircle2, 
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom duration-500" data-testid="dashboard">
        {/* Admin Alert for Pending Users */}
        {user?.role === 'admin' && stats?.pending_users > 0 && (
          <Link to="/users">
            <Card className="border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10 transition-colors cursor-pointer" data-testid="pending-users-alert">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-amber-500/20">
                      <Bell className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-500">
                        {stats.pending_users} neue Registrierung{stats.pending_users > 1 ? 'en' : ''} warten auf Genehmigung
                      </p>
                      <p className="text-sm text-muted-foreground">Klicken Sie hier, um zur Benutzerverwaltung zu gelangen</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-500 text-white">{stats.pending_users}</Badge>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Übersicht Ihrer Brandschutz-Abnahmen</p>
          </div>
          <Link to="/inspections/new">
            <Button className="h-12 px-6 gap-2" data-testid="new-inspection-button">
              <Plus className="h-5 w-5" />
              Neue Abnahme
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.title} className="border-border/50 card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} strokeWidth={1.5} />
                  </div>
                  <span className="font-heading font-bold text-3xl">{stat.value}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-3">{stat.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Inspections */}
        <Card className="border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl">Letzte Abnahmen</CardTitle>
            <Link to="/inspections">
              <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-inspections">
                Alle anzeigen
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent && stats.recent.length > 0 ? (
              <div className="space-y-4">
                {stats.recent.map((inspection) => {
                  const progress = calculateProgress(inspection.items);
                  const failCount = inspection.items?.filter(i => i.status === 'fail').length || 0;
                  
                  return (
                    <Link
                      key={inspection.id}
                      to={`/inspections/${inspection.id}`}
                      className="block"
                      data-testid={`inspection-${inspection.id}`}
                    >
                      <div className="p-4 rounded-lg border border-border/50 hover:border-primary/50 transition-all duration-200">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium truncate">
                                {inspection.property_name || inspection.property_address}
                              </h3>
                              {failCount > 0 && (
                                <span className="flex items-center gap-1 text-xs text-red-500">
                                  <AlertTriangle className="h-3 w-3" />
                                  {failCount}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {inspection.client_name} • {inspection.building_type}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getInspectionStatusColor(inspection.status)}`}>
                              {getInspectionStatusLabel(inspection.status)}
                            </span>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(inspection.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>Fortschritt</span>
                            <span>{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-1.5" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-muted-foreground">Noch keine Abnahmen vorhanden</p>
                <Link to="/inspections/new">
                  <Button variant="outline" className="mt-4 gap-2">
                    <Plus className="h-4 w-4" />
                    Erste Abnahme erstellen
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
